/**
 * Shared helper for calling the member-facing family-change operations
 * (#127 slice 5): `submitFamilyChangeRequest`, `previewFamilyChange`,
 * `myFamilyChangeRequests`, `respondFamilyChangeConsent`. All four are
 * dispatched by the same `submit-family-change-request` Lambda — see
 * `.agents/tasks/127-family-requests-contract.md`.
 *
 * Follows the same pattern as slice 4's
 * `frontend/src/components/admin/family/manageFamilyClient.ts`: the
 * `payload` argument (an `AWSJSON!`) is sent JSON-stringified, and every
 * response is parsed with `parseLambdaResponse`, which unwraps the double
 * JSON encoding AppSync Lambda resolvers use.
 */
import { generateClient } from 'aws-amplify/data';
import { parseLambdaResponse } from '../../../utils/parseLambdaResponse';

const client = generateClient({ authMode: 'userPool' });

export type FamilyChangeKind = 'NEW_PERSON' | 'EXISTING_MEMBER' | 'REMOVE_MEMBER' | 'PORTAL_ACCESS';

export interface NewPersonPayload {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender?: string;
  shirtSize?: string;
  email?: string;
  phone?: string;
  wantsPortalAccess?: boolean;
}

/** `submitFamilyChangeRequest('PORTAL_ACCESS', payload)` / `previewFamilyChange('PORTAL_ACCESS', payload)` (#150 slice 2). */
export interface PortalAccessPayload {
  memberId: string;
  email: string;
}

export interface PreviewResult {
  success: boolean;
  error?: string;
  message?: string;
  householdBefore?: { adults: number; juniors: number } | null;
  householdAfter?: { adults: number; juniors: number } | null;
  describeBefore?: string | null;
  describeAfter?: string | null;
  amountDueCents?: number;
}

export interface SubmitResult {
  success: boolean;
  error?: string;
  message?: string;
  requestId?: string;
}

export interface MyFamilyChangeRequest {
  id: string;
  kind: FamilyChangeKind;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  amountDueCents: number;
  paymentMethod: 'ONLINE' | 'AT_DESK' | 'NONE';
  paymentReceived: boolean;
  personFirstName?: string | null;
  personLastName?: string | null;
  removeMemberId?: string | null;
  /** PORTAL_ACCESS only (= existingMemberId; fine to return since the requester chose the member). */
  targetMemberId?: string | null;
  /** PORTAL_ACCESS: the person snapshot (firstName/lastName/email), for "Login request for [Name]". */
  person?: { firstName?: string | null; lastName?: string | null; email?: string | null } | null;
  decisionReason?: string | null;
  createdAt: string;
  awaitingMyConsent: boolean;
  requesterFirstName?: string | null;
}

export interface MyRequestsResult {
  success: boolean;
  error?: string;
  message?: string;
  requests?: MyFamilyChangeRequest[];
}

export interface ConsentResult {
  success: boolean;
  error?: string;
  message?: string;
  consent?: 'ACCEPTED' | 'DECLINED';
}

function toPayload(kind: FamilyChangeKind, payload: Record<string, unknown>): Record<string, unknown> {
  return { kind, payload: JSON.stringify(payload) };
}

/** No-write preview — same checks as submit, used for the before/after/amount display. */
export async function previewFamilyChange(
  kind: FamilyChangeKind,
  payload: Record<string, unknown>
): Promise<PreviewResult> {
  const raw = await (client.queries as any).previewFamilyChange(toPayload(kind, payload));
  const parsed = parseLambdaResponse<PreviewResult>(raw?.data);
  if (!parsed) throw new Error('No response received from the server. Please try again.');
  return parsed;
}

/** Creates the PENDING request. */
export async function submitFamilyChangeRequest(
  kind: FamilyChangeKind,
  payload: Record<string, unknown>
): Promise<SubmitResult> {
  const raw = await (client.mutations as any).submitFamilyChangeRequest(toPayload(kind, payload));
  const parsed = parseLambdaResponse<SubmitResult>(raw?.data);
  if (!parsed) throw new Error('No response received from the server. Please try again.');
  return parsed;
}

/** The caller's own requests, plus requests awaiting the caller's consent. */
export async function myFamilyChangeRequests(): Promise<MyRequestsResult> {
  const raw = await (client.queries as any).myFamilyChangeRequests({});
  const parsed = parseLambdaResponse<MyRequestsResult>(raw?.data);
  if (!parsed) throw new Error('No response received from the server. Please try again.');
  return parsed;
}

/** Accept or decline being added to someone else's family. */
export async function respondFamilyChangeConsent(
  requestId: string,
  accept: boolean
): Promise<ConsentResult> {
  const raw = await (client.mutations as any).respondFamilyChangeConsent({ requestId, accept });
  const parsed = parseLambdaResponse<ConsentResult>(raw?.data);
  if (!parsed) throw new Error('No response received from the server. Please try again.');
  return parsed;
}
